import React, {ReactNode, forwardRef} from 'react'
import {FormControl, InputGroup, Textarea as ChakraTextarea, FormLabel, FormErrorMessage} from '@chakra-ui/react'

interface TextareaProps {
    name: string
    label: string | ReactNode
    placeholder?: string
    rightIcon?: React.ReactNode
    isRequired?: boolean
    value: string
    errorMessage?: string
    isDisabled?: boolean
    isReadOnly?: boolean
    onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void
    onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void
}

/**
 * Textarea component renders a textarea input field with optional label, error message, and various states.
 *
 * @param {string} name - The name and id for the textarea element.
 * @param {string} label - The label text for the textarea.
 * @param {string} placeholder - The placeholder text for the textarea.
 * @param {boolean} isRequired - Indicates if the textarea is required.
 * @param {string} value - The value of the textarea.
 * @param {string} errorMessage - The error message to display when the textarea is invalid.
 * @param {boolean} isDisabled - Indicates if the textarea is disabled.
 * @param {boolean} isReadOnly - Indicates if the textarea is read-only.
 * @returns {JSX.Element} The rendered Textarea component.
 */

const Textarea = forwardRef<HTMLDivElement, TextareaProps>(
    ({name, label, placeholder, isRequired, value, errorMessage, isDisabled, isReadOnly, onChange, onBlur}, ref) => {
        return (
            <FormControl
                ref={ref}
                id={name}
                isRequired={isRequired}
                isInvalid={!!errorMessage}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                data-testid="textarea"
            >
                {label && <FormLabel>{label}</FormLabel>}
                <InputGroup>
                    <ChakraTextarea id={name} name={name} placeholder={placeholder} value={value} resize="none" onChange={onChange} onBlur={onBlur} />
                </InputGroup>
                {errorMessage && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
            </FormControl>
        )
    },
)

Textarea.displayName = 'Textarea'

export {Textarea}
